import { GraduationCap, Building2, Users, Quote, MapPin, Award } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

export default function About() {
  const { t } = useLang();
  const a = t.about;
  return (
    <section id="about" className="py-24 lg:py-32 relative overflow-hidden" data-testid="about-section">
      <div className="absolute inset-0 bbm-grid-bg opacity-60 pointer-events-none" />
      <div className="bbm-halo bg-[#FFB800] w-[360px] h-[360px] top-[10%] end-[-120px] opacity-25" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-16 items-end">
          <div className="lg:col-span-7">
            <p className="bbm-eyebrow mb-4">— {a.eyebrow}</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0B132B] leading-tight tracking-tight">
              {a.title}
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[#4B5563] text-lg leading-relaxed">{a.sub}</p>
          </div>
        </div>

        {/* Founder card */}
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Identity */}
          <div className="lg:col-span-5 bg-[#0B132B] text-white rounded-2xl p-8 lg:p-10 relative overflow-hidden flex flex-col" data-testid="founder-card">
            <div className="absolute inset-0 bbm-grid-bg-dark opacity-30 pointer-events-none" />
            <div className="relative flex-1 flex flex-col">
              <div className="flex items-center gap-2 mb-8">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FFB800]" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#FFB800]">{a.founder_role}</span>
              </div>

              <div className="font-display text-4xl lg:text-5xl mb-6 leading-tight" data-testid="founder-name">
                {a.founder_name}
              </div>
              <div className="w-12 h-px bg-[#FFB800] mb-6" />
              <p className="text-white/80 text-base leading-relaxed mb-10">{a.founder_lead}</p>

              <div className="mt-auto bg-white/5 border border-white/10 rounded-lg p-5 backdrop-blur">
                <Quote className="w-5 h-5 text-[#FFB800] mb-3" strokeWidth={1.5} />
                <p className="text-white text-sm italic leading-relaxed">"{a.quote}"</p>
              </div>
            </div>
          </div>

          {/* Credentials & ventures */}
          <div className="lg:col-span-7 grid gap-6">
            {/* Credentials */}
            <div className="bbm-card p-7" data-testid="credentials-card">
              <div className="flex items-center gap-2 mb-5">
                <Award className="w-4 h-4 text-[#0047FF]" />
                <span className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF]">
                  {a.credentials_label}
                </span>
              </div>
              <ul className="space-y-3">
                {a.credentials.map((c, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#0B132B] text-sm leading-relaxed" data-testid={`credential-${i}`}>
                    <span className="font-mono text-[10px] text-[#FFB800] mt-1 min-w-[20px]">
                      0{i + 1}
                    </span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Ventures grid */}
            <div className="bbm-card p-7" data-testid="ventures-card">
              <div className="flex items-center gap-2 mb-5">
                <Building2 className="w-4 h-4 text-[#0047FF]" />
                <span className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF]">
                  {a.ventures_label}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {a.ventures.map((v, i) => (
                  <div
                    key={i}
                    className="border border-[#E5E7EB] rounded-md p-3 flex items-start gap-3 hover:border-[#0B132B] transition-colors"
                    data-testid={`venture-${i}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FFB800] mt-2 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium text-[#0B132B] text-sm leading-tight">{v.name}</div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[#4B5563] mt-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {v.loc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Team */}
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-7 flex gap-4 items-start" data-testid="team-card">
              <div className="w-10 h-10 rounded-md bg-[#0047FF] flex items-center justify-center shrink-0">
                <Users className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-[#4B5563] mb-1">
                  {a.team_label}
                </div>
                <p className="text-[#0B132B] text-sm leading-relaxed">{a.team_text}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
